/**
 * <ClientAttributesDialog> — the ClientManager dialog (v2, plan
 * `ai-docs/lexide-navigation-and-client-manager-miniplan.md` §2.2), opened by
 * clicking a client row in the case navigator (operator 2026-07-06). v1 edited
 * `name` + `@alias`; v2 (operator-approved 2026-07-30) makes it the client's
 * single management surface:
 *
 *   - STATUS select — first UI consumer of the C.7 `client.set_status` RPC
 *     (active | prospect | inactive | former).
 *   - ESFERAS section — the client's matters, read-only rows; clicking one
 *     selects that scope in the panes (relocated from ClientDetailLayout,
 *     which N3 retires).
 *   - RESPONSÁVEIS (AssignmentsTab) + BRIEFING (ChatBriefingButton) — likewise
 *     relocated from the detail page.
 *   - ZONA DE RISCO — the client HARD delete (the platform's ONE ruled
 *     exception to no-hard-delete, 2026-07-30 v2): swaps this panel for the
 *     shared `ClientDeleteConfirm` (@lexiun/org-admin), driven by the lexserver
 *     delete-census. Cases are UNASSIGNED to «Não atribuídos», never deleted;
 *     legacy-shape cases block. Census 404 (P1 backend not shipped yet) renders
 *     the delete disabled — never as "zero cases".
 *
 * The alias is a `@client` reference handle: the server COERCES arbitrary input
 * to the tag grammar + dedupes per org (backend shipped 2026-07-06), so it is
 * NEVER rejected here — we save whatever the user types and render the FINAL
 * normalized alias the server returns ("Salvo como @…"), which may differ from
 * the input (e.g. `Trend Móveis` → `TrendMoveis`, or `TrendMoveis2` on collision).
 *
 * N3 retired ClientDetailLayout: this dialog IS the client surface — the old
 * "Abrir página completa" escape hatch is gone with the page.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, User, Users, X } from "lucide-react";
import {
  ClientDeleteConfirm,
  OrgAdminI18nProvider,
  type ClientDeleteCensus,
} from "@lexiun/org-admin";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogHeaderCls,
  dialogTitleCls,
  dialogCloseBtnCls,
  dialogLabelCls,
  dialogInputCls,
  dialogFooterCls,
  dialogCancelBtnCls,
  dialogPrimaryBtnCls,
} from "@/components/context-pane/dialog-styles";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useToastStore } from "@/stores/useToastStore";
import { useFormatters } from "@/lib/formatters";
import { useMatters } from "@/hooks/useMatters";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { AssignmentsTab } from "@/components/assignments/AssignmentsTab";
import { ChatBriefingButton } from "@/components/chat/ChatBriefingButton";
import { resolveDisplayName } from "@/lib/entity-display";
import { useLanguageStore } from "@/stores/useLanguageStore";
import { fetchClientDeleteCensus, type ClientListItem } from "@/lib/lexserver-client";
import type { ClientStatus } from "@lexiun/org-admin";

/**
 * DELETE ENABLE GATE — false until the runtime half is VERIFIABLY live.
 *
 * History (2026-07-30): flipped true on the builder's roll handback
 * (dev-20260730.1617-c052944b8, "artifact-grep-verified") — then the runtime
 * lane's discriminating symbol probe showed ac2ec7f3f NEVER MADE THAT IMAGE
 * (their probe found 5 sibling commits present, deleteClientInLexserver
 * absent; the builder's grep was a false positive). With the OLD handler
 * live, an enabled delete button wipes the workspace folder WITHOUT the
 * lexserver unassign — data loss, not just orphaning. Flipped back.
 *
 * FLIPPED TRUE on roll #2 (image dev-20260730.1831-ea8261734, fleet 8/8):
 * the builder's verification this time carried a DISCRIMINATING control
 * (dist-runtime stub: 0 hits) + the call site with the removed disk pre-check
 * + the gateway registration + ce39526ac's workspaceRemoved markers — and the
 * decisive proof is behavioral: the e2e §2b lifecycle (impact delete + server
 * post-checks) runs green against the rolled fleet. If a rollback ever
 * reopens the skew, set false again — the UI degrades to the honest
 * "backend pending" state everywhere.
 */
export const CLIENT_DELETE_BACKEND_READY = true;

const CLIENT_STATUSES: ClientStatus[] = ["active", "prospect", "inactive", "former"];

export interface ClientAttributesDialogProps {
  client: ClientListItem;
  onClose: () => void;
}

export function ClientAttributesDialog({ client, onClose }: ClientAttributesDialogProps) {
  const { t, i18n } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();
  const fmt = useFormatters();
  const { identity } = useAuthIdentity();
  const lang = useLanguageStore((s) => s.lang);

  // Map the app locale onto the shared org-admin locale set (same mapping
  // OrgAdminLayout.tsx uses) for the embedded ClientDeleteConfirm.
  const orgAdminLocale = i18n.language?.startsWith("pt")
    ? ("pt-BR" as const)
    : i18n.language?.startsWith("es")
      ? ("es-ES" as const)
      : ("en-US" as const);

  // ── v2: status · esferas · delete state ────────────────────────────────
  const [status, setStatus] = useState(client.status);
  const [statusSaving, setStatusSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: matters } = useMatters(client.clientId);

  // Delete census — fetched EAGERLY on dialog open so the danger-zone button
  // can label itself truthfully. THREE states, deliberately distinct
  // (unknown-must-be-representable): `undefined` = still loading / errored
  // (button disabled, no claim made) · `null` = endpoint 404, P1 backend not
  // shipped (button disabled, "backend pending") · census = real answer.
  const censusQuery = useQuery({
    queryKey: ["client-delete-census", client.clientId],
    queryFn: () => fetchClientDeleteCensus(client.clientId),
    staleTime: 15_000,
    retry: false,
  });
  const census: ClientDeleteCensus | null | undefined = censusQuery.data;

  const deleteAvailable = CLIENT_DELETE_BACKEND_READY && census != null;
  const deleteUnavailableReason = !CLIENT_DELETE_BACKEND_READY || census === null
    ? t("clientAttributes.deletePendingBackend")
    : censusQuery.isError
      ? t("clientAttributes.deleteCensusFailed")
      : undefined;

  const activeClientId = useActiveContextStore((s) => s.selection?.clientId);
  const selectMatter = useActiveContextStore((s) => s.selectMatter);

  const handleStatusChange = useCallback(
    async (next: string) => {
      if (!rpc || statusSaving || next === status) return;
      setStatusSaving(true);
      const prev = status;
      setStatus(next as typeof status);
      try {
        await rpc.clientSetStatus({ clientId: client.clientId, status: next });
        void queryClient.invalidateQueries({ queryKey: ["clients"] });
        void queryClient.invalidateQueries({ queryKey: ["client", client.clientId] });
        addToast({ text: t("clientAttributes.statusSaved"), kind: "success", autoDismissMs: 3000 });
      } catch {
        setStatus(prev); // roll the optimistic select back — the server refused
        addToast({ text: t("clientAttributes.errors.generic"), kind: "error", autoDismissMs: 4000 });
      } finally {
        setStatusSaving(false);
      }
    },
    [rpc, statusSaving, status, client.clientId, queryClient, addToast, t],
  );

  const handleDelete = useCallback(async () => {
    if (!rpc || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      // force:true — the ruled delete removes the workspace folder too; the
      // registry commit is the success signal either way (workspaceRemoved is
      // advisory — ce39526ac).
      const res = await rpc.clientDelete({ clientId: client.clientId, force: true });
      if (res.ok) {
        // The cases were UNASSIGNED server-side — refresh everything that
        // renders client names or case parentage, and drop the scope if the
        // deleted client was the active one.
        void queryClient.invalidateQueries({ queryKey: ["clients"] });
        void queryClient.invalidateQueries({ queryKey: ["cases"] });
        void queryClient.invalidateQueries({ queryKey: ["matters"] });
        // ⚠ The CASOS tree reads its OWN key. Without this, the deleted
        // client's group lingers until the 60s stale refetch — caught by the
        // §2b e2e (server had deleted + unassigned; only this pane lagged).
        void queryClient.invalidateQueries({ queryKey: ["overview-cases"] });
        if (activeClientId === client.clientId) {
          useActiveContextStore.getState().clear();
        }
        addToast({
          text: t("clientAttributes.deleted", { name: client.name }),
          kind: "success",
          autoDismissMs: 4000,
        });
        onClose();
        return;
      }
      // Typed refusal (ok:false — the case.refresh convention, P2 handback):
      // CONFLICT = legacy-shape cases; FORBIDDEN = self-client (unreachable
      // here, the danger zone hides on self-clients, but the copy exists).
      setDeleteError(
        res.code === "CONFLICT"
          ? t("clientAttributes.deleteConflict")
          : res.message ?? t("clientAttributes.errors.generic"),
      );
    } catch {
      setDeleteError(t("clientAttributes.errors.generic"));
    } finally {
      setDeleting(false);
    }
  }, [rpc, deleting, client.clientId, client.name, queryClient, activeClientId, addToast, t, onClose]);

  const [name, setName] = useState(client.name);
  const [alias, setAlias] = useState(client.alias ?? "");
  // Baseline (last-persisted values) the dirty-check diffs against — starts at the
  // props, then advances on a successful save. The parent's `client` prop is a
  // snapshot that does NOT refresh while the dialog is open, so diffing against it
  // directly would leave Save "enabled" after saving.
  const [baseName, setBaseName] = useState(client.name);
  const [baseAlias, setBaseAlias] = useState(client.alias ?? "");
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  // The server-normalized alias after a successful save (may differ from input);
  // null until an alias-changing save this session.
  const [savedAlias, setSavedAlias] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // Reset when the target client changes (the parent reuses one dialog instance).
  useEffect(() => {
    setName(client.name);
    setAlias(client.alias ?? "");
    setBaseName(client.name);
    setBaseAlias(client.alias ?? "");
    setSaving(false);
    setServerError(null);
    setSavedAlias(null);
  }, [client.clientId, client.name, client.alias]);

  useEffect(() => {
    nameRef.current?.select();
  }, []);

  // Only send changed fields. A blank alias input CLEARS a previously-set alias
  // (distinct from "unchanged"); a blank NAME is never sent (name is required).
  const trimmedName = name.trim();
  const nameChanged = trimmedName !== "" && trimmedName !== baseName;
  const aliasInput = alias.trim().replace(/^@/, "");
  const aliasChanged = aliasInput !== baseAlias;
  const canSave = !saving && trimmedName !== "" && (nameChanged || aliasChanged);

  const handleSave = useCallback(async () => {
    if (!rpc || !canSave) return;
    setSaving(true);
    setServerError(null);
    setSavedAlias(null);
    try {
      const res = await rpc.clientUpdate({
        clientId: client.clientId,
        ...(nameChanged ? { name: trimmedName } : {}),
        ...(aliasChanged ? { alias: aliasInput } : {}),
      });
      if (res.ok) {
        // Refresh every client-name/alias surface (navigator, reverse title bar,
        // detail page). The `client.updated` broadcast also fires server-side; this
        // is the immediate, don't-wait-for-the-round-trip refresh.
        void queryClient.invalidateQueries({ queryKey: ["clients"] });
        void queryClient.invalidateQueries({ queryKey: ["client", client.clientId] });
        const finalAlias = res.alias ?? "";
        setName(res.name);
        setAlias(finalAlias);
        // Advance the baseline so the Save button reflects "no unsaved changes".
        setBaseName(res.name);
        setBaseAlias(finalAlias);
        // Surface the normalized alias ONLY when THIS save changed the alias — a
        // name-only save on an alias-less client must not read as "alias cleared".
        setSavedAlias(aliasChanged ? finalAlias : null);
        addToast({
          text: aliasChanged && finalAlias
            ? t("clientAttributes.savedAs", { alias: finalAlias })
            : t("clientAttributes.saved"),
          kind: "success",
          autoDismissMs: 3500,
        });
        return;
      }
      setServerError(t("clientAttributes.errors.generic"));
    } catch {
      setServerError(t("clientAttributes.errors.generic"));
    } finally {
      setSaving(false);
    }
  }, [rpc, canSave, client.clientId, nameChanged, aliasChanged, trimmedName, aliasInput, queryClient, addToast, t]);

  // Escape closes (backdrop click does NOT — a misclick must not discard edits,
  // matching the file attributes dialog).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onEnterSave = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    }
  };

  const inputCls = dialogInputCls;

  // Danger-zone branch: the confirm REPLACES the panel inside the same overlay
  // (one modal at a time; Escape still cancels via the confirm's own handler).
  if (deleteOpen && census) {
    return (
      <div data-testid="client-attributes-dialog" className={dialogOverlayCls}>
        <OrgAdminI18nProvider locale={orgAdminLocale}>
          <ClientDeleteConfirm
            client={client}
            census={census}
            deleting={deleting}
            error={deleteError ?? undefined}
            onConfirm={() => void handleDelete()}
            onCancel={() => {
              setDeleteOpen(false);
              setDeleteError(null);
            }}
          />
        </OrgAdminI18nProvider>
      </div>
    );
  }

  return (
    <div data-testid="client-attributes-dialog" className={dialogOverlayCls}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("clientAttributes.title")}
        onClick={(e) => e.stopPropagation()}
        className={dialogPanelCls}
      >
        {/* Header */}
        <div className={dialogHeaderCls}>
          <h2 className={cn(dialogTitleCls, "flex items-center gap-1.5")}>
            {client.isPerson ? (
              <User size={14} className="text-cream-500" aria-hidden />
            ) : (
              <Users size={14} className="text-cream-500" aria-hidden />
            )}
            {t("clientAttributes.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("actions.close")}
            className={dialogCloseBtnCls}
          >
            <X size={14} />
          </button>
        </div>

        {/* Meta row — the STATUS is now EDITABLE (v2, first consumer of the
            C.7 client.set_status RPC); the rest stays read-only. The select is
            optimistic with rollback — see handleStatusChange. */}
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-cream-500">
          {/* The SELECT is the status display — no badge beside it. (v2 first
              shipped both and the badge's raw en-US chip next to the localized
              select read as two disagreeing statuses.) */}
          <span className="inline-flex items-center gap-1.5">
            <select
              data-testid="client-status-select"
              value={status}
              disabled={statusSaving}
              onChange={(e) => void handleStatusChange(e.target.value)}
              aria-label={t("clientAttributes.statusLabel")}
              className="rounded border border-cream-200/80 bg-transparent px-1 py-0.5 text-caption text-cream-500 focus:outline-none dark:border-coffee-700/60 dark:text-cream-300"
            >
              {CLIENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`clientAttributes.statusValues.${s}`)}
                </option>
              ))}
            </select>
            {statusSaving && <Loader2 size={11} className="animate-spin" aria-hidden />}
          </span>
          <span>{client.isPerson ? t("clientAttributes.person") : t("clientAttributes.company")}</span>
          {/* matterCount dropped from the meta row (v2): the cached list row's
              count lagged the live Esferas section below and the two
              disagreeing numbers read as a bug. The section heading carries
              the live count. */}
          {client.createdAt && (
            <span>{t("clientAttributes.created", { date: fmt.date(client.createdAt) })}</span>
          )}
        </div>

        {/* Full / legal name */}
        <label className={dialogLabelCls}>{t("clientAttributes.nameLabel")}</label>
        <input
          ref={nameRef}
          data-testid="client-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onEnterSave}
          placeholder={t("clientAttributes.namePlaceholder")}
          maxLength={200}
          className={inputCls}
        />

        {/* Alias (@handle) — coerced server-side, never rejected here */}
        <label className={cn(dialogLabelCls, "mt-4")}>{t("clientAttributes.aliasLabel")}</label>
        <input
          data-testid="client-alias-input"
          value={alias.replace(/^@/, "")}
          onChange={(e) => {
            setAlias(e.target.value.replace(/^@/, ""));
            setSavedAlias(null);
          }}
          onKeyDown={onEnterSave}
          placeholder={t("clientAttributes.aliasPlaceholder")}
          maxLength={40}
          className={cn(inputCls, "font-mono font-medium")}
        />
        {savedAlias !== null ? (
          <p
            className="mt-1 text-caption text-emerald-600 dark:text-emerald-400"
            data-testid="client-alias-saved"
          >
            {savedAlias
              ? t("clientAttributes.savedAs", { alias: savedAlias })
              : t("clientAttributes.aliasCleared")}
          </p>
        ) : (
          <p className="mt-1 text-caption text-cream-500">{t("clientAttributes.aliasHint")}</p>
        )}

        {serverError && <p className="mt-2 text-caption text-red-500">{serverError}</p>}

        {/* ── ESFERAS (v2, relocated from ClientDetailLayout — N3) ─────────
            Read-only rows; clicking one SELECTS that scope in the panes and
            closes the dialog — navigation stays in-flow, no page exit. */}
        {(matters ?? []).length > 0 && (
          <section className="mt-5" data-testid="client-matters-section">
            <h3 className="mb-1 text-caption font-semibold uppercase tracking-wide text-cream-500">
              {t("clientAttributes.mattersHeading", { count: (matters ?? []).length })}
            </h3>
            <ul className="max-h-32 overflow-y-auto">
              {(matters ?? []).map((m) => (
                <li key={m.matterId}>
                  <button
                    type="button"
                    data-testid="client-matter-row"
                    onClick={() => {
                      selectMatter(client.clientId, m.matterId);
                      onClose();
                    }}
                    className="w-full truncate rounded px-1.5 py-1 text-left text-body-sm text-cream-500 hover:bg-cream-100/70 dark:text-cream-300 dark:hover:bg-coffee-800/60"
                  >
                    {resolveDisplayName(m, lang) || m.matterId}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── RESPONSÁVEIS (v2, relocated from ClientDetailLayout — N3) ──── */}
        <section className="mt-5" data-testid="client-assignments-section">
          <AssignmentsTab scope="client" clientId={client.clientId} clientName={client.name} />
        </section>

        {/* ── BRIEFING (v2, relocated from ClientDetailLayout — N3) ──────── */}
        <section className="mt-4" data-testid="client-briefing-section">
          <ChatBriefingButton
            tier="client"
            label={client.name}
            clientId={client.clientId}
            userId={identity?.userId}
            orgId={identity?.orgId}
          />
        </section>

        {/* ── ZONA DE RISCO (v2) — the ruled hard-delete exception ─────────
            Self-clients never offer delete (server 403s anyway). The button is
            title-explained when disabled: census pending (P1) or runtime not
            yet hardened (P2 gate const above). */}
        {!client.isSelfClient && (
          <section className="mt-6 border-t border-red-200/60 pt-3 dark:border-red-900/40">
            <h3 className="text-caption font-semibold uppercase tracking-wide text-red-600/80 dark:text-red-400/80">
              {t("clientAttributes.dangerZone")}
            </h3>
            <button
              type="button"
              data-testid="client-delete-open"
              disabled={!deleteAvailable}
              title={deleteUnavailableReason}
              onClick={() => setDeleteOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-red-300/70 px-2.5 py-1.5 text-body-sm text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-950/30"
            >
              <Trash2 size={13} aria-hidden />
              {t("clientAttributes.deleteAction")}
            </button>
            {deleteUnavailableReason && (
              <p className="mt-1 text-caption text-cream-500">{deleteUnavailableReason}</p>
            )}
          </section>
        )}

        {/* Footer — the "Abrir página completa" escape hatch died with
            ClientDetailLayout (N3): the dialog IS the client surface now. */}
        <div className={cn(dialogFooterCls, "justify-end")}>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className={dialogCancelBtnCls}>
              {t("actions.cancel")}
            </button>
            <button
              type="button"
              data-testid="client-attributes-save"
              onClick={() => void handleSave()}
              disabled={!canSave}
              className={dialogPrimaryBtnCls(canSave)}
            >
              {saving && <Loader2 size={13} className="animate-spin" aria-hidden />}
              {t("clientAttributes.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
